import React, { useMemo } from 'react'
import { Box, TextField, Typography, Stack, Button, MenuItem } from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { FormTextField, FormSelect } from '../../../../../GlobalComponents/form-components';
import { useForm, FormProvider } from "react-hook-form";
import { useAuthSession } from '../../../../../Auth';
import { useSearchParams } from 'react-router-dom';
import { useBatchUploadContactsMutation, useAgents, useCreateSingleContactMutation, } from '../api';
import LinearProgress from '@mui/material/LinearProgress';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import ErrorButton from "../error-handling/ErrorButton";
import { error } from 'console';
import { AxiosError } from 'axios';
import { useAllContacts } from '../api';
import { useDebounce } from 'use-debounce';

type AddSingleContactFormData = {
  household_id: string;
  agent_email: string;
};

export default function AddSingleContact() {
  const [searchParams] = useSearchParams();
  const list_id = searchParams.get("id");
  const { name: user_name }= useAuthSession();
  const methods = useForm<AddSingleContactFormData>({
    defaultValues: {
      household_id: "",
      agent_email: "",
    },
  });

  const household_id = methods.watch("household_id");
  const agent_email = methods.watch("agent_email");

  const [debouncedHHID] = useDebounce(household_id, 500);

  const { data: existingContacts } = useAllContacts({list_id: list_id || ""});

  const isDuplicate = useMemo(() => {
    if(!existingContacts || existingContacts.length === 0) return false;
    if(debouncedHHID.length <= 5) return false;
    return !!existingContacts.find((c: Contact) => c.household_number === debouncedHHID);
  },[existingContacts, debouncedHHID]);

  const { mutate, isPending, isSuccess, error: mutateError } = useCreateSingleContactMutation({
    onSuccess: () => {
      methods.reset({
        household_id: "",
        agent_email: "",
      });
    },
  });

  const { data: agents, isLoading } = useAgents();


  const onSubmit = (data: AddSingleContactFormData) => {
    console.log("data", data);
    const body: PostSingleContactBody = {
      created_by: user_name,
      ...data,
      list_name: searchParams.get("name") || "",
    };

    mutate(body);
  };

  return (
    <FormProvider {...methods}>
      <Box
        sx={{
          background: "#fff",
          borderRadius: 1,
          paddingLeft: 3,
          paddingBottom: 4,
          paddingTop: 2,
        }}
      >
        <Stack flexDirection="row" gap={2} alignItems="flex-end">
          <Stack style={{ width: "30%", height: 45 }}>
            <Typography
              style={{ fontWeight: "bold", fontSize: 13, paddingLeft: 5 }}
            >
              Household ID
            </Typography>
            <FormTextField
              name="household_id"
              required
              InputProps={{
                style: { height: 35 },
              }}
              size="small"
              placeholder="Enter Household ID"
              customError={isDuplicate ? "Duplicate Household ID" : undefined}
            />
          </Stack>
          <Stack style={{ width: "30%", height: 45 }}>
            <Typography
              style={{ fontWeight: "bold", fontSize: 13, paddingLeft: 5 }}
            >
              Agent Email
            </Typography>
            <FormSelect
              name="agent_email"
              size="small"
              sx={{
                height: 35,
              }}
              required
            >
              <MenuItem value="" disabled>
                Select Agent Email
              </MenuItem>
              {agents?.map((v: Agent) => (
                <MenuItem key={v.email} value={v.email}>
                  {v.email}
                </MenuItem>
              ))}
            </FormSelect>
          </Stack>
          <Stack style={{ width: "30%", height: 45 }}>
            <Typography
              style={{
                visibility: "hidden", // Or opacity: 0
                fontWeight: "bold",
                fontSize: 14,
                paddingLeft: 5,
              }}
            >
              Spacer
            </Typography>
            {isPending ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  height: 35,
                  paddingBottom: 0.5,
                }}
              >
                <LinearProgress sx={{ width: "100%" }} />
              </Box>
            ) : mutateError ? (
              <ErrorButton
                error={mutateError as AxiosError}
                onRetry={methods.handleSubmit(onSubmit)}
              />
            ) : (
              <Button
                sx={{ height: 35 }}
                variant="contained"
                disabled={isDuplicate}
                startIcon={
                  isSuccess && !agent_email && !household_id ? (
                    <DoneOutlineIcon />
                  ) : (
                    <PersonAddAlt1Icon />
                  )
                }
                onClick={methods.handleSubmit(onSubmit)}
                color={
                  isSuccess && !agent_email && !household_id
                    ? "success"
                    : "primary"
                }
              >
                {isSuccess && !agent_email && !household_id
                  ? "Contact Added"
                  : "Add to List"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </FormProvider>
  );
}
